import { cloneElement, forwardRef, isValidElement } from 'react';

interface ButtonBaseProps {
  variant?: 'primary' | 'secondary' | 'outline' | 'danger' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  disabled?: boolean;
  className?: string;
  children: React.ReactNode;
}

interface ButtonAsButtonProps extends ButtonBaseProps {
  asChild?: false;
  type?: 'button' | 'submit' | 'reset';
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

interface ButtonAsChildProps extends ButtonBaseProps {
  asChild: true;
}

type ButtonProps = ButtonAsButtonProps | ButtonAsChildProps;

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      disabled = false,
      className = '',
      children,
      ...props
    },
    ref
  ) => {
    const baseClasses =
      'inline-flex min-h-10 items-center justify-center rounded-lg font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';

    const variantClasses = {
      primary:
        'bg-warehouse-600 text-white shadow-sm hover:bg-warehouse-700 focus-visible:ring-warehouse-500',
      secondary:
        'bg-warehouse-100 text-warehouse-700 hover:bg-warehouse-200 focus-visible:ring-warehouse-500',
      outline:
        'border border-navy-300 bg-white text-navy-700 shadow-sm hover:bg-navy-50 focus-visible:ring-warehouse-500',
      danger:
        'bg-red-600 text-white shadow-sm hover:bg-red-700 focus-visible:ring-red-500',
      ghost:
        'text-navy-600 hover:bg-navy-100 hover:text-navy-900 focus-visible:ring-warehouse-500',
    };

    const sizeClasses = {
      sm: 'px-3 py-1.5 text-sm',
      md: 'px-4 py-2 text-sm',
      lg: 'px-5 py-2.5 text-base',
    };

    const classes = `${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`;

    if ('asChild' in props && props.asChild) {
      if (!isValidElement<{ className?: string; 'aria-disabled'?: boolean; tabIndex?: number }>(children)) {
        throw new Error('Button with asChild requires exactly one valid React element child.');
      }

      return cloneElement(children, {
        className: `${classes} ${children.props.className || ''}`.trim(),
        'aria-disabled': disabled || undefined,
        tabIndex: disabled ? -1 : children.props.tabIndex,
      });
    }

    const { asChild: _asChild, ...buttonProps } = props as ButtonAsButtonProps;

    return (
      <button
        ref={ref}
        className={classes}
        disabled={disabled || loading}
        {...buttonProps}
      >
        {loading ? (
          <svg
            className="-ml-1 mr-2 h-4 w-4 animate-spin"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        ) : null}
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;
